import { useEffect, useMemo, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Card } from '../../components/Card';
import { Container } from '../../components/Container';
import styles from './styles.module.css';
import { Button } from '../../components/Button';
import Close from '../../components/svg/Close';
import { Link, useNavigate } from 'react-router-dom';
import ArrowDown from '../../components/svg/ArrowDown';
import ArrowUp from '../../components/svg/ArrowUp';
import Plus from '../../components/svg/Plus';
import { useWallet } from '@solana/wallet-adapter-react';
import { createToken, LABELS } from '../../services/web3Service';
import {
  CreateTokenFormData,
  uploadIpfsData,
} from '../../services/ipfsService';
import toast from 'react-hot-toast';
import { LoaderV2 } from '../../components/LoaderV2';
import { BaseWalletMultiButton } from '@solana/wallet-adapter-react-ui';
import InfoV2 from '../../components/svg/InfoV2';
import CloseV2 from '../../components/svg/CloseV2';
import SuccessV2 from '../../components/svg/SuccessV2';
import ErrorV2 from '../../components/svg/ErrorV2';

interface IpfsResponse {
  IpfsHash: string;
  PinSize: number;
  Timestamp: string;
}

export const Create = () => {
  const navigate = useNavigate();
  const wallet = useWallet();
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm();
  const watchFields = watch(['tokenImage']);
  const [isMoreOptions, setIsMoreOptions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit: SubmitHandler<any> = (data: CreateTokenFormData) => {
    try {
      setIsSubmitting(true);
      const requestData = async (data: CreateTokenFormData) => {
        let ipfsData: IpfsResponse = await uploadIpfsData(data);
        const result = await createToken(wallet, {
          name: data.tokenName,
          symbol: data.tokenTicker,
          uri: ipfsData.IpfsHash,
        });
        const mint = await result.toString();
        return mint;
      };
      toast.promise(
        requestData(data),
        {
          loading: (
            <div className={styles.toastLoading}>
              <LoaderV2 />
              <span className={styles.toastText}>Loading</span>
            </div>
          ),
          success: (data) => {
            setIsSubmitting(false);
            localStorage.setItem('createdToken', data);
            toast(
              (t) => (
                <div className={styles.toastInfo}>
                  <InfoV2 />
                  <span className={styles.toastText}>View token details</span>
                  <Link
                    to={`/${data}`}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.tokenLink}
                  >
                    View token
                  </Link>
                  <CloseV2
                    className={styles.dismissToast}
                    onClick={() => toast.dismiss(t.id)}
                  />
                </div>
              ),
              { duration: Infinity }
            );
            return (
              <div className={styles.toastSuccess}>
                <SuccessV2 />
                <span className={styles.toastText}>
                  The token was successfully created
                </span>
              </div>
            );
          },
          error: (err) => {
            setIsSubmitting(false);
            console.log(err);
            return (
              <div className={styles.toastError}>
                <ErrorV2 />
                <span className={styles.toastText}>Approval denied</span>
              </div>
            );
          },
        },
        {
          success: {
            duration: 5000,
          },
        }
      );
    } catch (error: any) {
      console.log(error);
      toast.error(
        <div className={styles.toastError}>
          <ErrorV2 />
          <span className={styles.toastText}>{error.message}</span>
        </div>
      );
    }
  };

  const imageUploaded = useMemo(() => {
    return watchFields[0]?.length ? true : false;
  }, [watchFields]);

  useEffect(() => {
    console.log(watchFields);
  }, [watchFields]);

  return (
    <div className={styles.container}>
      <Container>
        <div className={styles.col}>
          <Card className={styles.card}>
            <div className={styles.header}>
              <div className={styles.title}>Create token</div>
              <Link
                to={'..'}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(-1);
                }}
                className={styles.close}
              >
                <Close />
              </Link>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
              <label className={styles.inputContainer}>
                Name*
                <input
                  type="text"
                  className={styles.input}
                  placeholder="Enter the token name"
                  {...register('tokenName', {
                    required: true,
                    validate: (value) =>
                      value.length <= 32 ||
                      'Token name length must be less than or equal to 32',
                  })}
                />
                {errors.tokenName && (
                  <span className={styles.error}>
                    {errors.tokenName.message?.toString() ||
                      'This field is required'}
                  </span>
                )}
              </label>

              <label className={styles.inputContainer}>
                Ticker*
                <input
                  type="text"
                  className={styles.input}
                  placeholder="Enter the token ticker"
                  {...register('tokenTicker', {
                    required: true,
                    validate: (value) =>
                      value.length <= 10 ||
                      'Token ticker length must be less than or equal to 10',
                  })}
                />
                {errors.tokenTicker && (
                  <span className={styles.error}>
                    {errors.tokenTicker.message?.toString() ||
                      'This field is required'}
                  </span>
                )}
              </label>
              <div className={styles.inputContainer}>
                Image*
                <input
                  type="file"
                  id="file"
                  className={styles.input}
                  accept="image/jpeg, image/png"
                  {...register('tokenImage', {
                    required: true,
                    validate: (value) =>
                      value[0].size <= 10000000 || 'File size is too big',
                  })}
                />
                {!imageUploaded && <div className={styles.inputFile} />}
                <label htmlFor="file" className={styles.fileLabel}>
                  upload image
                  <Plus style={{ width: 31, height: 31 }} />
                </label>
                {errors.tokenImage && (
                  <span className={styles.error}>
                    {errors.tokenImage.message?.toString() ||
                      'This field is required'}
                  </span>
                )}
              </div>
              <label className={styles.inputContainer}>
                Description
                <textarea
                  className={styles.textarea}
                  placeholder="Write the token description"
                  {...register('tokenDescription', {
                    required: false,
                    validate: (value) =>
                      value.length <= 500 ||
                      'Token description length must be less than or equal to 500',
                  })}
                />
                {errors.tokenDescription && (
                  <span className={styles.error}>
                    {errors.tokenDescription.message?.toString() ||
                      'This field is required'}
                  </span>
                )}
              </label>
              <div
                className={styles.moreOptionsSpoiler}
                onClick={() => setIsMoreOptions(!isMoreOptions)}
              >
                {isMoreOptions ? (
                  <ArrowUp
                    style={{ width: 16, height: 16, color: '#00FF94' }}
                  />
                ) : (
                  <ArrowDown
                    style={{ width: 16, height: 16, color: '#00FF94' }}
                  />
                )}
                <span>{isMoreOptions && 'hide'} more options</span>
              </div>
              {isMoreOptions && (
                <>
                  <label className={styles.inputContainer}>
                    twitter link
                    <input
                      type="text"
                      className={styles.input}
                      placeholder="optional"
                      {...register('twitterLink')}
                    />
                  </label>
                  <label className={styles.inputContainer}>
                    telegram link
                    <input
                      type="text"
                      className={styles.input}
                      placeholder="optional"
                      {...register('telegramLink')}
                    />
                  </label>
                  <label className={styles.inputContainer}>
                    website
                    <input
                      type="text"
                      className={styles.input}
                      placeholder="optional"
                      {...register('website')}
                    />
                  </label>
                </>
              )}
              {wallet?.publicKey ? (
                <Button
                  type="submit"
                  className={styles.submitButton}
                  disabled={isSubmitting}
                >
                  Create token
                </Button>
              ) : (
                <BaseWalletMultiButton
                  style={{
                    width: '100%',
                    background:
                      'linear-gradient(90deg, #08b26a 0%, #42598a 50%, #9f01ff 100%)',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: 18,
                    lineHeight: 24,
                    borderRadius: 100,
                    fontFamily: 'Pangram, sans-serif',
                  }}
                  labels={{ ...LABELS, 'no-wallet': 'Connect wallet' }}
                />
              )}
              <div className={styles.disclaimerWrapper}>
                <div className={styles.disclaimerTitle}>Cost to deploy</div>
                <div className={styles.disclaimerText}>-0,02 SOL</div>
              </div>
            </form>
          </Card>
        </div>
      </Container>
    </div>
  );
};
